/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Typography } from '@mui/material';
import { groupBy } from 'lodash';
import type { FC } from 'react';
import React from 'react';
import type { SortableEvent } from 'react-sortablejs';
import { ReactSortable } from 'react-sortablejs';
import type { Position } from '@opengeoweb/shared';
import { ToolContainerDraggable } from '@opengeoweb/shared';
import { PlotRows } from './PlotRows';
import { RowLayout } from './RowLayout';
import type {
  MoveParameterPayload,
  UpdateTitlePayload,
} from '../../store/types';
import { useTimeseriesTranslation } from '../../utils/i18n';
import type { Parameter, Plot, PlotPreset } from '../TimeSeries/types';
import { AddPlotButton } from '../TimeSeriesAddPlot/TimeSeriesAddPlot';

const timeSeriesManagerStyle = {
  '.timeSeriesManagerContainer': {
    containerType: 'inline-size',
  },
  '.medium-timeseriesmanager': {
    display: 'none',
  },
  // Styles when Time Series Manager width is less than 620px
  '@container (max-width: 620px)': {
    '.timeseriesmanager': {
      '.medium-timeseriesmanager': {
        display: 'block',
      },
      '.plotRow': {
        display: 'none',
      },
      '.tooltipSelectWidth': {
        width: '100%',
      },
    },
    '.timeSeriesManagerHeaderRow': {
      justifyContent: 'flex-start',

      '.timeSeriesManagerParameter, .timeseriesManagerDimension': {
        display: 'none',
      },
    },
    '.timeSeriesManagerStyle': {
      display: 'none',
    },
    '.timeSeriesManagerParameter, .timeSeriesManagerCollection': {
      width: 'auto',
    },
  },
};

export const styles = {
  headerText: {
    fontSize: 12,
    opacity: 0.67,
  },
};

export interface CustomSortableEvent extends SortableEvent {
  oldIndex: number;
  newIndex: number;
}

export const TimeSeriesManager: FC<{
  panelId: string;
  onClose: () => void;
  onMouseDown: () => void;
  isOpen: boolean;
  order: number;
  plotState: PlotPreset;
  addPlot: (title: string) => void;
  deletePlot: (id: string) => void;
  togglePlot: (id: string) => void;
  addParameter: (parameter: Parameter) => void;
  deleteParameter: (id: string) => void;
  toggleParameter: (id: string) => void;
  selectPlotId: string;
  setSelectPlotId: (id: string) => void;
  startPosition?: Position;
  patchParameter: (parameter: Partial<Parameter>) => void;
  movePlot: (event: CustomSortableEvent) => void;
  moveParameter: (event: MoveParameterPayload) => void;
  updateTitle: (payload: UpdateTitlePayload) => void;
  startSize?: { width: number; height: number };
}> = ({
  panelId,
  onClose,
  isOpen,
  onMouseDown,
  order,
  plotState,
  addPlot,
  deletePlot,
  togglePlot,
  addParameter,
  deleteParameter,
  toggleParameter,
  selectPlotId,
  setSelectPlotId,
  startPosition = { top: 85, left: 50 },
  patchParameter,
  movePlot,
  moveParameter,
  updateTitle,
  startSize = { width: 900, height: 300 },
}) => {
  const [isSorting, setIsSorting] = React.useState(false);
  const { t } = useTimeseriesTranslation();
  const parametersGroupedByPlot = groupBy(
    plotState.parameters,
    (parameter) => parameter.plotId,
  );

  const plotHierarchy: Plot[] = plotState.plots.map((plot) => {
    const plotParameters = parametersGroupedByPlot[plot.plotId];
    return { ...plot, parameters: plotParameters };
  });

  const onSortEnd = React.useCallback(
    (event: SortableEvent): void => {
      const { oldIndex, newIndex } = event;
      if (oldIndex !== undefined && newIndex !== undefined) {
        movePlot({ oldIndex, newIndex } as CustomSortableEvent);
      }
    },
    [movePlot],
  );

  const onStart = React.useCallback(() => {
    setIsSorting(true);
  }, []);

  const onEnd = React.useCallback(() => {
    setIsSorting(false);
  }, []);

  const showTooltip = !isSorting;

  return (
    <ToolContainerDraggable
      className="timeseriesmanager"
      isOpen={isOpen}
      minHeight={126}
      minWidth={320}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      startPosition={startPosition}
      startSize={startSize}
      sx={{ ...timeSeriesManagerStyle }}
      title="Time Series Manager"
    >
      <Box className="timeSeriesManagerContainer" sx={{ padding: '0 6px' }}>
        <RowLayout
          className="timeSeriesManagerHeaderRow"
          collection={
            <Typography sx={{ fontSize: 12, opacity: 0.67 }}>
              {t('timeseries-collection')}
            </Typography>
          }
          dimension={
            <Typography sx={{ fontSize: 12, opacity: 0.67 }}>
              {t('timeseries-dimension')}
            </Typography>
          }
          parameter={
            <Typography sx={{ fontSize: 12, opacity: 0.67 }}>
              {t('timeseries-parameter')}
            </Typography>
          }
          plot={
            <AddPlotButton handleClick={addPlot} plotState={plotState} t={t} />
          }
          style={
            <Typography sx={{ fontSize: 12, opacity: 0.67 }}>
              {t('timeseries-style')}
            </Typography>
          }
          sx={{ height: 48 }}
        />
        <ReactSortable
          animation={200}
          direction="vertical"
          group={{ name: 'plot', pull: true, put: ['plot'] }}
          list={plotHierarchy.map((plot) => ({ id: plot.plotId }))}
          onEnd={onEnd}
          onSort={onSortEnd}
          onStart={onStart}
          setList={(): void => {}}
          swapThreshold={0.3} // Nested sortable gets jumpy without this. Cant be lower because e2e will fail.
        >
          {plotHierarchy.map((plot, i) => (
            <PlotRows
              addParameter={addParameter}
              deleteParameter={deleteParameter}
              deletePlot={deletePlot}
              key={plot.plotId}
              panelId={panelId}
              moveParameter={moveParameter}
              patchParameter={patchParameter}
              plot={plot}
              rowIndex={i}
              selectPlotId={selectPlotId}
              setSelectPlotId={setSelectPlotId}
              showTooltip={showTooltip}
              t={t}
              toggleParameter={toggleParameter}
              togglePlot={togglePlot}
              updateTitle={updateTitle}
            />
          ))}
        </ReactSortable>
      </Box>
    </ToolContainerDraggable>
  );
};
